import React from 'react';
import { useTranslation } from 'react-i18next';
import SessionMapCell from './SessionMapCell';

function StudentCell({ student, printHref, onRevokeScanToken, revokingStudentId }) {
  const unsubmitted = student.submitted === false;
  const href = printHref ? printHref(student) : null;
  const revoking = revokingStudentId === student.id;
  return (
    <div className="flex items-center gap-3">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium shrink-0 ${
        unsubmitted
          ? 'bg-gray-100 text-gray-400 dark:bg-gray-700/50 dark:text-gray-500'
          : 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
      }`}>
        {student.initials}
      </div>
      <span className={`font-medium truncate ${
        unsubmitted
          ? 'text-gray-400 dark:text-gray-500'
          : 'text-gray-900 dark:text-white'
      }`}>
        {student.full_name}
      </span>
      {(href || (student.scan_token_active && onRevokeScanToken)) && (
        <div className="ml-auto flex items-center gap-3 shrink-0">
          {href && (
            <a
              href={href}
              target="_blank"
              rel="noopener"
              onClick={(e) => e.stopPropagation()}
              title="Print scannable worksheet"
              aria-label={`Print scannable worksheet for ${student.full_name}`}
              className="shrink-0 text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-gray-200 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" />
              </svg>
            </a>
          )}
          {student.scan_token_active && onRevokeScanToken && (
            <button
              type="button"
              disabled={revoking}
              onClick={(e) => {
                e.stopPropagation();
                onRevokeScanToken(student);
              }}
              title="Revoke printed worksheet access"
              aria-label={`Revoke printed worksheet access for ${student.full_name}`}
              className="shrink-0 text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400 transition-colors disabled:opacity-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75">
                <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636a9 9 0 11-12.728 0M12 3v9" />
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function SessionMapGrid({
  questions,
  students,
  gridData,
  emptyStudentsMessage: emptyStudentsProp,
  emptyQuestionsMessage: emptyQuestionsProp,
  onCellClick,
  selectedCell, // { studentId, questionId } — highlights a single cell
  expandedStudentId,
  renderExpandedRow,
  printHref, // optional (student) => string; renders a per-row print-worksheet icon
  onRevokeScanToken, // optional (student) => void; renders a revoke icon when student.scan_token_active
  revokingStudentId, // optional — id of the student currently being revoked, disables that row's button
}) {
  const { t } = useTranslation();
  const emptyStudentsMessage = emptyStudentsProp || t('sessionMap.noStudentsYet');
  const emptyQuestionsMessage = emptyQuestionsProp || t('sessionMap.noApprovedQuestions');
  // Build lookup for efficient rendering
  const gridLookup = gridData.reduce((acc, row) => {
    acc[row.student_id] = row;
    return acc;
  }, {});

  if (students.length === 0) {
    return (
      <div className="p-12 text-center text-gray-500 dark:text-gray-400">
        {emptyStudentsMessage}
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="p-12 text-center text-gray-500 dark:text-gray-400">
        {emptyQuestionsMessage}
      </div>
    );
  }

  return (
    <table className="min-w-full">
      <thead>
        <tr className="bg-gray-50 dark:bg-gray-800">
          <th className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-800 px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider min-w-[180px]">
            {t('common.student')}
          </th>
          {questions.map((q) => (
            <th
              key={q.id}
              className="px-2 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider min-w-[80px]"
              title={q.prompt_preview}
            >
              Q{q.position}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
        {students.map((student) => {
          const studentRow = gridLookup[student.id];
          const isExpanded = expandedStudentId === student.id;
          return (
            <React.Fragment key={student.id}>
              <tr className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
                student.submitted === false ? 'bg-gray-50/50 dark:bg-gray-900/40' : ''
              }`}>
                <td className={`sticky left-0 z-10 px-4 py-3 ${
                  student.submitted === false
                    ? 'bg-gray-50/80 dark:bg-gray-900/60'
                    : 'bg-white dark:bg-gray-900'
                }`}>
                  <StudentCell
                    student={student}
                    printHref={printHref}
                    onRevokeScanToken={onRevokeScanToken}
                    revokingStudentId={revokingStudentId}
                  />
                </td>
                {questions.map((q) => {
                  const cellData = studentRow?.responses?.[q.id];
                  const isCurrent = student.current_question_id === q.id;
                  const isSelected = selectedCell?.studentId === student.id && selectedCell?.questionId === q.id;
                  return (
                    <td
                      key={q.id}
                      className="px-2 py-2"
                    >
                      <SessionMapCell
                        cellData={cellData}
                        isCurrent={isCurrent}
                        isSelected={isSelected}
                        needsReview={cellData?.needs_review}
                        correct={cellData?.correct}
                        onClick={onCellClick ? () => onCellClick({ studentId: student.id, questionId: q.id }) : undefined}
                      />
                    </td>
                  );
                })}
              </tr>
              {isExpanded && renderExpandedRow && (
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <td colSpan={questions.length + 1} className="p-0">
                    {renderExpandedRow(student, studentRow)}
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}
